import { useState, useEffect, useRef } from 'react';
import { cxcApi, parametrosApi } from '../../utils/api';
import { FileText, Search, DollarSign, AlertCircle, AlertTriangle, CheckCircle, Clock, X, Check, ChevronLeft, ChevronRight, Calendar, Eye, Delete, CheckSquare, Square, FileSpreadsheet, FileDown, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import PrintPreviewModal from '../../components/PrintPreviewModal';
import useAuthStore from '../../store/authStore';
import useToolbarResponsive from '../../hooks/useToolbarResponsive';
import useAlertHighlight from '../../hooks/useAlertHighlight';
import AlertHighlightChip from '../../components/AlertHighlightChip';
import MonthRangePicker from '../../components/MonthRangePicker';

import useFiltroPeriodo from '../../hooks/useFiltroPeriodo';
// Toolbar button component
function ToolbarButton({ icon: Icon, label, onClick, disabled, variant = 'default', badge }) {
  const baseClasses = "flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px]";
  const variants = {
    default: "border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400",
    primary: "border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500",
    success: "border border-green-400 text-green-600 hover:bg-green-50 hover:border-green-500",
    danger: "border border-red-400 text-red-500 hover:bg-red-50 hover:border-red-500",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} relative`}
    >
      <Icon className="h-5 w-5 mb-1" />
      <span className="whitespace-nowrap">{label}</span>
      {badge && (
        <span className="absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
          {badge}
        </span>
      )}
    </button>
  );
}

const TIPO_DOC_LABELS = {
  '01': 'FE',
  '02': 'ND',
  '03': 'NC',
  '04': 'TE',
  '08': 'FEC',
  '09': 'FEE'
};

// Opciones de filtro de fecha
const DATE_FILTER_OPTIONS = [
  { value: '', label: 'Todas las fechas' },
  { value: 'hoy', label: 'Hoy' },
  { value: 'ayer', label: 'Ayer' },
  { value: 'semana', label: 'Esta semana' },
  { value: 'mes', label: 'Este mes' },
  { value: 'mes_anterior', label: 'Mes anterior' },
  { value: 'ano', label: 'Este año' },
  { value: 'ano_anterior', label: 'Año anterior' },
  { value: 'custom', label: 'Personalizado' }
];

// Helper para calcular rangos de fecha
const getDateRange = (preset) => {
  const hoy = dayjs();
  switch (preset) {
    case 'hoy':
      return { desde: hoy.format('YYYY-MM-DD'), hasta: hoy.format('YYYY-MM-DD') };
    case 'ayer':
      const ayer = hoy.subtract(1, 'day');
      return { desde: ayer.format('YYYY-MM-DD'), hasta: ayer.format('YYYY-MM-DD') };
    case 'semana':
      return { desde: hoy.startOf('week').format('YYYY-MM-DD'), hasta: hoy.endOf('week').format('YYYY-MM-DD') };
    case 'mes':
      return { desde: hoy.startOf('month').format('YYYY-MM-DD'), hasta: hoy.endOf('month').format('YYYY-MM-DD') };
    case 'mes_anterior':
      const mesAnterior = hoy.subtract(1, 'month');
      return { desde: mesAnterior.startOf('month').format('YYYY-MM-DD'), hasta: mesAnterior.endOf('month').format('YYYY-MM-DD') };
    case 'ano':
      return { desde: hoy.startOf('year').format('YYYY-MM-DD'), hasta: hoy.endOf('year').format('YYYY-MM-DD') };
    case 'ano_anterior':
      const anoAnterior = hoy.subtract(1, 'year');
      return { desde: anoAnterior.startOf('year').format('YYYY-MM-DD'), hasta: anoAnterior.endOf('year').format('YYYY-MM-DD') };
    default:
      return { desde: '', hasta: '' };
  }
};

export default function FacturasPendientes() {
  const { currentEmisor } = useAuthStore();

  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  // Filtro de período unificado (MonthRangePicker). Default = mes actual.
  // Si el user llega desde una Smart Alert del dashboard (?highlight=...) el
  // filtro se cambia automáticamente a "Todo el tiempo" (efecto más abajo) para
  // que las FE vencidas de meses anteriores aparezcan. Al limpiar el chip, vuelve a mes actual.
  const buildMesActualFiltro = () => {
    const n = new Date();
    const y = n.getFullYear();
    const m = String(n.getMonth() + 1).padStart(2, '0');
    const last = new Date(y, n.getMonth() + 1, 0).getDate();
    return { modo: 'mes', periodo: `${y}-${m}`, desde: `${y}-${m}-01`, hasta: `${y}-${m}-${String(last).padStart(2, '0')}` };
  };
  const [filtroFecha, setFiltroFecha] = useFiltroPeriodo('FacturasPendientes', buildMesActualFiltro);
  const [bancos, setBancos] = useState([]);

  // Sort
  const [sortColumn, setSortColumn] = useState('fecha');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
  };

  // Modal de pago
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState(null);

  // Preview modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFacturaId, setPreviewFacturaId] = useState(null);

  // Confirm modal for delete
  const [confirmModal, setConfirmModal] = useState(null);

  // Selection state
  const [selectedFacturas, setSelectedFacturas] = useState([]);
  const { searchRef, buttonsRef } = useToolbarResponsive([filtroFecha, filtroEstado, selectedFacturas.length]);

  // Ref para anclar verticalmente el chip flotante de Smart Alert
  const tableHeaderRef = useRef(null);

  // Highlight desde alertas inteligentes del dashboard (?highlight=vencidas).
  // Marca las filas que matchean con un pulso ámbar y hace scroll al primer match.
  const { isHighlighted, animOn, highlightedRef, hasMatches, matchedIds, clearHighlight, highlightKey } = useAlertHighlight({
    items: facturas,
    getId: (f) => f.id,
    matcher: {
      vencidas: (f) => f.fechaVencimiento && new Date(f.fechaVencimiento) < new Date() && f.estadoPago !== 'pagado'
    }
  });

  useEffect(() => {
    loadBancos();
  }, []);

  // Carga facturas por estado, período y búsqueda. La búsqueda usa debounce y, cuando
  // hay término, el backend ignora el filtro de mes (busca en todo el histórico).
  useEffect(() => {
    const t = setTimeout(loadData, search ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroEstado, filtroFecha, search]);

  // Sincroniza filtro con highlightKey:
  //   - Si llega un highlight (vienes de una Smart Alert) → filtro = "Todo el tiempo"
  //     (para que las FE vencidas de meses anteriores aparezcan).
  //   - Si limpiás el chip (clearHighlight) → filtro vuelve a "Mes actual".
  //   - Al montar sin highlight (o al recargar con F5) se conserva el período que se tenía.
  const highlightAnteriorRef = useRef(null);
  useEffect(() => {
    const antes = highlightAnteriorRef.current;
    highlightAnteriorRef.current = highlightKey;
    if (highlightKey) {
      setFiltroFecha({ modo: 'rango', desde: '', hasta: '' });
    } else if (antes) {
      setFiltroFecha(buildMesActualFiltro());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightKey]);

  // (filtro de fecha viejo eliminado — todo lo maneja MonthRangePicker)

  const loadBancos = async () => {
    try {
      const response = await parametrosApi.listBancosActivos();
      setBancos(response.data.data || []);
    } catch (error) {
      console.error('Error loading bancos:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filtroEstado) params.estado = filtroEstado;
      const searchTerm = (search || '').trim();
      if (searchTerm) {
        // Búsqueda inteligente: manda el término y NO el período (busca en todo el histórico)
        params.search = searchTerm;
      } else {
        if (filtroFecha?.desde) params.fechaDesde = filtroFecha.desde;
        if (filtroFecha?.hasta) params.fechaHasta = filtroFecha.hasta;
      }

      const facturasRes = await cxcApi.listFacturas(params);
      setFacturas(facturasRes.data.data || []);
    } catch (error) {
      console.error('Error loading CxC:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const filteredFacturas = facturas.filter(f => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      f.consecutivo?.toLowerCase().includes(searchLower) ||
      f.receptorNombre?.toLowerCase().includes(searchLower) ||
      f.receptorIdentificacion?.includes(searchLower)
    );
  }).sort((a, b) => {
    let valA, valB;
    switch (sortColumn) {
      case 'fecha': valA = new Date(a.fechaEmision); valB = new Date(b.fechaEmision); break;
      case 'tipo': valA = a.tipoDocumento || ''; valB = b.tipoDocumento || ''; break;
      case 'numero': valA = a.consecutivo || ''; valB = b.consecutivo || ''; break;
      case 'cliente': valA = (a.receptorNombre || '').toLowerCase(); valB = (b.receptorNombre || '').toLowerCase(); break;
      case 'vencimiento': valA = new Date(a.fechaVencimiento || '2099-12-31'); valB = new Date(b.fechaVencimiento || '2099-12-31'); break;
      case 'total': valA = parseFloat(a.totalComprobante) || 0; valB = parseFloat(b.totalComprobante) || 0; break;
      case 'cobrado': valA = parseFloat(a.totalPagado) || 0; valB = parseFloat(b.totalPagado) || 0; break;
      case 'saldo': valA = parseFloat(a.saldoPendiente) || 0; valB = parseFloat(b.saldoPendiente) || 0; break;
      case 'estado': {
        // Ordenar por días hasta vencimiento (lo que muestra el badge visual):
        // - asc: vencidas más antiguas → vencidas recientes → al día → por vencer cerca → por vencer lejos.
        // - desc: lo opuesto.
        // Sin fecha de vencimiento → al final (Infinity).
        const today = new Date(); today.setHours(0, 0, 0, 0);
        valA = a.fechaVencimiento ? (new Date(a.fechaVencimiento).getTime() - today.getTime()) : Infinity;
        valB = b.fechaVencimiento ? (new Date(b.fechaVencimiento).getTime() - today.getTime()) : Infinity;
        break;
      }
      default: return 0;
    }
    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const openPagoModal = (factura) => {
    setSelectedFactura(factura);
    setShowPagoModal(true);
  };

  // ========================================
  // SELECTION FUNCTIONS
  // ========================================

  const toggleFacturaSelection = (factura) => {
    setSelectedFacturas(prev => {
      const isSelected = prev.some(f => f.id === factura.id);
      if (isSelected) {
        return prev.filter(f => f.id !== factura.id);
      } else {
        return [...prev, factura];
      }
    });
  };

  const toggleSelectAll = () => {
    const eligibleFacturas = filteredFacturas.filter(f => f.estadoPago !== 'pagado');
    if (selectedFacturas.length === eligibleFacturas.length && eligibleFacturas.length > 0) {
      setSelectedFacturas([]);
    } else {
      setSelectedFacturas(eligibleFacturas);
    }
  };

  const clearSelection = () => {
    setSelectedFacturas([]);
  };

  // Check if selected invoices can be collected together (same client and currency)
  const canCobrarSelected = () => {
    if (selectedFacturas.length === 0) return false;
    const first = selectedFacturas[0];
    return selectedFacturas.every(f =>
      f.receptorId === first.receptorId &&
      f.moneda === first.moneda &&
      f.estadoPago !== 'pagado'
    );
  };

  const getSelectedTotal = () => {
    return selectedFacturas.reduce((sum, f) => sum + (f.saldoPendiente || f.totalComprobante || 0), 0);
  };

  const getSelectedMoneda = () => {
    return selectedFacturas.length > 0 ? (selectedFacturas[0].moneda || 'CRC') : 'CRC';
  };

  const handleVerSelected = () => {
    if (selectedFacturas.length === 1) {
      setPreviewFacturaId(selectedFacturas[0].id);
      setShowPreviewModal(true);
    }
  };

  const handleCobrarSelected = () => {
    if (!canCobrarSelected()) {
      toast.error('Las facturas deben ser del mismo cliente y moneda');
      return;
    }
    if (selectedFacturas.length === 1) {
      setSelectedFactura(selectedFacturas[0]);
    }
    setShowPagoModal(true);
  };

  const handleEliminarSelected = async () => {
    if (selectedFacturas.length === 1) {
      setConfirmModal({
        title: '¿Eliminar factura?',
        message: `Se eliminará la factura ${selectedFacturas[0].consecutivo}`,
        onConfirm: async () => {
          try {
            await cxcApi.deleteFactura(selectedFacturas[0].id);
            toast.success('Factura eliminada');
            setSelectedFacturas([]);
            loadData();
          } catch (error) {
            toast.error('Error al eliminar factura');
          }
          setConfirmModal(null);
        }
      });
    } else {
      setConfirmModal({
        title: `¿Eliminar ${selectedFacturas.length} facturas?`,
        message: `Se eliminarán ${selectedFacturas.length} facturas seleccionadas. Esta acción no se puede deshacer.`,
        onConfirm: async () => {
          try {
            for (const factura of selectedFacturas) {
              await cxcApi.deleteFactura(factura.id);
            }
            toast.success(`${selectedFacturas.length} facturas eliminadas`);
            setSelectedFacturas([]);
            loadData();
          } catch (error) {
            toast.error('Error al eliminar facturas');
          }
          setConfirmModal(null);
        }
      });
    }
  };

  const formatMoney = (amount, moneda = 'CRC') => {
    const num = parseFloat(amount) || 0;
    if (moneda === 'USD') {
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `₡${num.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Export functions
  const [exporting, setExporting] = useState(false);

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (filtroEstado) params.append('estado', filtroEstado);
      if (filtroFecha?.desde) params.append('fechaDesde', filtroFecha.desde);
      if (filtroFecha?.hasta) params.append('fechaHasta', filtroFecha.hasta);

      const response = await cxcApi.exportFacturasExcel(params.toString());
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CxC_Facturas_${dayjs().format('YYYY-MM-DD')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exportado correctamente');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Error al exportar Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (filtroEstado) params.append('estado', filtroEstado);
      if (filtroFecha?.desde) params.append('fechaDesde', filtroFecha.desde);
      if (filtroFecha?.hasta) params.append('fechaHasta', filtroFecha.hasta);

      const response = await cxcApi.exportFacturasPdf(params.toString());
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CxC_Facturas_${dayjs().format('YYYY-MM-DD')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exportado correctamente');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Error al exportar PDF');
    } finally {
      setExporting(false);
    }
  };

  const isVencida = (fecha) => {
    if (!fecha) return false;
    return new Date(fecha) < new Date();
  };

  // Intelligent estado badge with days until due
  const getEstadoBadge = (factura) => {
    const { estadoPago, fechaVencimiento, totalPagado, totalComprobante } = factura;

    // Si ya está cobrado
    if (estadoPago === 'pagado') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3" />
          Cobrado
        </span>
      );
    }

    // Si tiene cobro parcial
    if (estadoPago === 'parcial') {
      const porcentaje = totalPagado && totalComprobante
        ? Math.round((totalPagado / totalComprobante) * 100)
        : 0;
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <DollarSign className="h-3 w-3" />
          Parcial {porcentaje}%
        </span>
      );
    }

    // Calcular días para vencimiento
    const fechaVenc = fechaVencimiento
      ? dayjs(fechaVencimiento)
      : dayjs(factura.fechaEmision).add(30, 'day');

    const hoy = dayjs().startOf('day');
    const vence = fechaVenc.startOf('day');
    const dias = vence.diff(hoy, 'day');

    if (dias > 10) {
      return <span className="whitespace-nowrap px-2 py-0.5 text-xs border border-green-300 text-green-700 rounded">Vence {dias}d</span>;
    } else if (dias > 1) {
      return <span className="whitespace-nowrap px-2 py-0.5 text-xs border border-yellow-300 text-yellow-700 rounded">Vence {dias}d</span>;
    } else if (dias === 1) {
      return <span className="whitespace-nowrap px-2 py-0.5 text-xs border border-orange-300 text-orange-700 rounded">Mañana</span>;
    } else if (dias === 0) {
      return <span className="whitespace-nowrap px-2 py-0.5 text-xs font-medium border border-red-300 text-red-700 rounded">Hoy</span>;
    } else {
      return <span className="whitespace-nowrap px-2 py-0.5 text-xs font-medium border border-red-400 text-red-700 rounded">Venció {Math.abs(dias)}d</span>;
    }
  };

  return (
    <div className="flex flex-col gap-6 h-[var(--alto-contenido)] min-w-0">
      {/* Dashboard Cards - KPIs de Cobranza */}
      {(() => {
        const hoy = new Date();
        const en8Dias = new Date(hoy.getTime() + 8 * 24 * 60 * 60 * 1000);
        const en15Dias = new Date(hoy.getTime() + 15 * 24 * 60 * 60 * 1000);

        // Calcular totales pendientes por moneda
        const pendientes = { CRC: 0, USD: 0, cantCRC: 0, cantUSD: 0 };
        const vencidos = { CRC: 0, USD: 0, cantCRC: 0, cantUSD: 0 };
        const proximos8 = { CRC: 0, USD: 0, cant: 0 };
        const proximos15 = { CRC: 0, USD: 0, cant: 0 };

        facturas.forEach(f => {
          if (f.estadoPago === 'pagado') return;

          const mon = f.moneda || 'CRC';
          const saldo = parseFloat(f.saldoPendiente || f.totalComprobante) || 0;
          const fechaVenc = f.fechaVencimiento ? new Date(f.fechaVencimiento) : null;

          // Total pendiente
          pendientes[mon] = (pendientes[mon] || 0) + saldo;
          if (mon === 'CRC') pendientes.cantCRC++;
          else pendientes.cantUSD++;

          // Vencidas
          if (fechaVenc && fechaVenc < hoy) {
            vencidos[mon] = (vencidos[mon] || 0) + saldo;
            if (mon === 'CRC') vencidos.cantCRC++;
            else vencidos.cantUSD++;
          }

          // Próximos a vencer (no vencidas aún)
          if (fechaVenc && fechaVenc >= hoy) {
            if (fechaVenc <= en8Dias) {
              proximos8[mon] = (proximos8[mon] || 0) + saldo;
              proximos8.cant++;
            } else if (fechaVenc <= en15Dias) {
              proximos15[mon] = (proximos15[mon] || 0) + saldo;
              proximos15.cant++;
            }
          }
        });

        const totalPendientes = pendientes.cantCRC + pendientes.cantUSD;
        const totalVencidos = vencidos.cantCRC + vencidos.cantUSD;

        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 shrink-0">
            {/* Card 1: Total Por Cobrar */}
            <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
              <div className="px-4 py-2 border-b bg-gradient-to-r from-green-50 to-green-100/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-green-600 rounded">
                    <DollarSign className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="font-semibold text-sm text-gray-900">Por Cobrar</h3>
                </div>
                <span className="text-xs text-gray-500">{totalPendientes} fact.</span>
              </div>
              <div className="px-4 py-2 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">₡</span>
                  <span className="text-sm font-bold text-orange-600 ">
                    {(pendientes.CRC || 0).toLocaleString('es-CR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">$</span>
                  <span className="text-sm font-bold text-blue-600 ">
                    {(pendientes.USD || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                </div>
              </div>
            </div>

            {/* Card 2: Total Vencido */}
            <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
              <div className={`px-4 py-2 border-b flex items-center justify-between ${totalVencidos > 0 ? 'bg-gradient-to-r from-red-50 to-red-100/50' : 'bg-gradient-to-r from-gray-50 to-gray-100/50'}`}>
                <div className="flex items-center gap-2">
                  <div className={`p-1.5 rounded ${totalVencidos > 0 ? 'bg-red-600' : 'bg-gray-400'}`}>
                    <AlertCircle className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="font-semibold text-sm text-gray-900">Vencido</h3>
                </div>
                <span className="text-xs text-gray-500">{totalVencidos > 0 ? `${totalVencidos} fact.` : 'Al día'}</span>
              </div>
              <div className="px-4 py-2 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">₡</span>
                  <span className={`text-sm font-bold  ${vencidos.CRC > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                    {(vencidos.CRC || 0).toLocaleString('es-CR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">$</span>
                  <span className={`text-sm font-bold  ${vencidos.USD > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                    {(vencidos.USD || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                </div>
              </div>
            </div>

            {/* Card 3: Próximos a Vencer */}
            <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
              <div className="px-4 py-2 border-b bg-gradient-to-r from-amber-50 to-amber-100/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-amber-500 rounded">
                    <Clock className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="font-semibold text-sm text-gray-900">Próximos a Vencer</h3>
                </div>
              </div>
              <div className="px-4 py-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">8 días</span>
                  <span className="text-xs text-gray-600">₡{(proximos8.CRC || 0).toLocaleString('es-CR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                  <span className="text-xs text-gray-600">${(proximos8.USD || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded">15 días</span>
                  <span className="text-xs text-gray-600">₡{(proximos15.CRC || 0).toLocaleString('es-CR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                  <span className="text-xs text-gray-600">${(proximos15.USD || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Toolbar — buscador absolute izq, botones centrados al toolbar (responsive con hook) */}
      <div className="bg-white rounded-lg border px-4 py-3 shrink-0 relative">
        <div ref={searchRef} className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 w-52 text-sm"
            />
          </div>
          <MonthRangePicker
            value={filtroFecha}
            onChange={setFiltroFecha}
            onRefresh={loadData}
          />
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="px-2 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Todos</option>
            <option value="pendiente">Pendiente</option>
            <option value="parcial">Parcial</option>
          </select>
        </div>
        <div ref={buttonsRef} className="flex items-center justify-center gap-2 flex-wrap">
          <ToolbarButton
            icon={Eye}
            label="Ver"
            variant="primary"
            onClick={handleVerSelected}
            disabled={selectedFacturas.length !== 1}
          />
          <ToolbarButton
            icon={DollarSign}
            label="Cobrar"
            variant="success"
            onClick={handleCobrarSelected}
            disabled={!canCobrarSelected()}
            badge={selectedFacturas.length > 0 ? selectedFacturas.length : null}
          />

          <div className="w-px h-10 bg-gray-300 mx-2" />

          <ToolbarButton
            icon={FileSpreadsheet}
            label="Excel"
            variant="success"
            onClick={handleExportExcel}
            disabled={exporting || filteredFacturas.length === 0}
          />
          <ToolbarButton
            icon={FileDown}
            label="PDF"
            variant="danger"
            onClick={handleExportPdf}
            disabled={exporting || filteredFacturas.length === 0}
          />

          {/* Selection info */}
          {selectedFacturas.length > 0 && (
            <>
              <div className="w-px h-10 bg-gray-300 mx-2" />
              <div className="flex items-center gap-3 text-sm">
                <span className="text-gray-500">
                  <span className="font-semibold text-gray-900">{selectedFacturas.length}</span> seleccionadas
                </span>
                {canCobrarSelected() && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className={`font-bold ${getSelectedMoneda() === 'USD' ? 'text-blue-600' : 'text-gray-600'}`}>
                      {formatMoney(getSelectedTotal(), getSelectedMoneda())}
                    </span>
                  </>
                )}
                <button
                  onClick={clearSelection}
                  className="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 border border-gray-300 rounded-lg transition-colors"
                >
                  <X size={14} />
                  Limpiar
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden flex-1 min-h-0 flex flex-col">
        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-2 text-gray-500">Cargando...</p>
          </div>
        ) : filteredFacturas.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto" />
            <p className="mt-2 text-gray-500">No hay facturas pendientes de cobro</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full" ref={tableHeaderRef}>
              <thead className="bg-gray-50 border-b sticky top-0 z-10">
                <tr>
                  {/* Checkbox column */}
                  <th className="px-2 py-3 w-10">
                    <button
                      onClick={toggleSelectAll}
                      className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                      title={selectedFacturas.length === filteredFacturas.filter(f => f.estadoPago !== 'pagado').length && selectedFacturas.length > 0 ? 'Deseleccionar todo' : 'Seleccionar todo'}
                    >
                      {selectedFacturas.length === filteredFacturas.filter(f => f.estadoPago !== 'pagado').length && selectedFacturas.length > 0
                        ? <CheckSquare size={18} className="text-green-600" />
                        : <Square size={18} />
                      }
                    </button>
                  </th>
                  {[
                    { id: 'fecha', label: 'Fecha', align: 'left' },
                    { id: 'tipo', label: 'Tipo', align: 'left' },
                    { id: 'numero', label: 'No. Documento', align: 'left' },
                    { id: 'cliente', label: 'Cliente', align: 'left', extra: 'min-w-[200px]' },
                    { id: 'vencimiento', label: 'Vencimiento', align: 'left' },
                    { id: 'total', label: 'Total', align: 'right' },
                    { id: 'cobrado', label: 'Cobrado', align: 'right' },
                    { id: 'saldo', label: 'Saldo', align: 'right' },
                    { id: 'estado', label: 'Estado', align: 'center' },
                  ].map(col => (
                    <th
                      key={col.id}
                      onClick={() => handleSort(col.id)}
                      className={`px-4 py-3 text-${col.align} text-xs font-semibold cursor-pointer select-none whitespace-nowrap hover:bg-gray-100 ${col.extra || ''} ${sortColumn === col.id ? 'bg-blue-50 text-blue-700' : 'text-gray-600'}`}
                    >
                      <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : col.align === 'center' ? 'justify-center' : ''}`}>
                        {col.label}
                        {sortColumn === col.id ? (
                          sortDirection === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                        ) : (
                          <ArrowUpDown size={12} className="text-gray-300" />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredFacturas.map((f) => {
                  const isSelected = selectedFacturas.some(sf => sf.id === f.id);
                  const canSelect = f.estadoPago !== 'pagado';

                  // Check if compatible for multiple payment (same client and currency)
                  const firstSelected = selectedFacturas[0];
                  const isCompatibleForPayment = !firstSelected || isSelected || (
                    f.receptorId === firstSelected.receptorId &&
                    f.moneda === firstSelected.moneda
                  );
                  const canSelectForPayment = canSelect && isCompatibleForPayment;

                  // Es la primera fila destacada por la alerta → le ponemos el ref para el auto-scroll
                  const isFirstHighlighted = isHighlighted(f.id) && matchedIds.values().next().value === f.id;
                  const hl = isHighlighted(f.id);
                  return (
                    <tr
                      key={f.id}
                      ref={isFirstHighlighted ? highlightedRef : null}
                      onClick={() => canSelectForPayment && toggleFacturaSelection(f)}
                      className={clsx(
                        'cursor-pointer',
                        hl && animOn && 'row-highlight-info',
                        hl && !animOn && 'row-highlight-info-static',
                        !hl && isSelected && 'bg-green-50 hover:bg-green-100',
                        !hl && !isSelected && isVencida(f.fechaVencimiento) && f.estadoPago !== 'pagado' && 'bg-red-50 hover:bg-red-100',
                        !hl && !isSelected && !isVencida(f.fechaVencimiento) && 'hover:bg-gray-50',
                        !canSelectForPayment && 'opacity-40 cursor-not-allowed'
                      )}
                    >
                      {/* Checkbox */}
                      <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
                        {canSelectForPayment ? (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleFacturaSelection(f)}
                            className="h-4 w-4 text-green-600 rounded border-gray-300 focus:ring-green-500 cursor-pointer"
                          />
                        ) : (
                          <span className="h-4 w-4 inline-block" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {dayjs(f.fechaEmision).format('DD/MM/YYYY')}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-medium bg-gray-100 px-2 py-1 rounded">
                          {TIPO_DOC_LABELS[f.tipoDocumento] || f.tipoDocumento}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono">{f.consecutivo}</td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{f.receptorNombre}</p>
                        <p className="text-xs text-gray-500">{f.receptorIdentificacion}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={clsx(
                          'text-sm',
                          isVencida(f.fechaVencimiento) && f.estadoPago !== 'pagado' ? 'text-red-600 font-medium' : 'text-gray-500'
                        )}>
                          {f.fechaVencimiento ? dayjs(f.fechaVencimiento).format('DD/MM/YYYY') : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        {formatMoney(f.totalComprobante, f.moneda)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        {(f.totalPagado || 0) > 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                            {formatMoney(f.totalPagado, f.moneda)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-bold">
                        <span className={f.moneda === 'USD' ? 'text-blue-600' : 'text-gray-600'}>
                          {formatMoney(f.saldoPendiente || f.totalComprobante, f.moneda)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getEstadoBadge(f)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Chip flotante centrado al viewport — solo cuando hay highlight activo */}
      <AlertHighlightChip
        highlightKey={highlightKey}
        hasMatches={hasMatches}
        count={matchedIds.size}
        onClose={clearHighlight}
        anchorRef={tableHeaderRef}
      />

      {/* Modal de Pago */}
      {showPagoModal && (selectedFactura || selectedFacturas.length > 0) && (
        <PagoModal
          factura={selectedFacturas.length === 1 ? selectedFacturas[0] : selectedFactura}
          facturas={selectedFacturas}
          isMultiple={selectedFacturas.length > 1}
          bancos={bancos}
          onClose={() => {
            setShowPagoModal(false);
            setSelectedFactura(null);
          }}
          onSuccess={() => {
            setShowPagoModal(false);
            setSelectedFactura(null);
            setSelectedFacturas([]);
            loadData();
          }}
        />
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{confirmModal.title}</h3>
              <p className="text-gray-600">{confirmModal.message}</p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => setConfirmModal(null)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmModal.onConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewFacturaId && (
        <PrintPreviewModal
          documentId={previewFacturaId}
          emisor={currentEmisor}
          onClose={() => {
            setShowPreviewModal(false);
            setPreviewFacturaId(null);
          }}
        />
      )}
    </div>
  );
}

// Payment Modal Component - Same style as CxP
function PagoModal({ factura, facturas = [], isMultiple = false, bancos = [], onClose, onSuccess }) {
  // For multiple: use first invoice for currency, sum all balances
  const invoicesToPay = isMultiple ? facturas : [factura];
  const moneda = invoicesToPay[0]?.moneda || 'CRC';
  // Round to 2 decimals to avoid floating point issues
  const saldoPendiente = Math.round((isMultiple
    ? invoicesToPay.reduce((sum, f) => sum + (f.saldoPendiente || f.totalComprobante || 0), 0)
    : (factura?.saldoPendiente || factura?.totalComprobante || 0)) * 100) / 100;

  const [formData, setFormData] = useState({
    fechaPago: dayjs().format('YYYY-MM-DD'),
    monto: saldoPendiente.toFixed(2),
    medioPago: 'transferencia',
    numeroComprobante: '',
    banco: ''
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aplicarRetencion, setAplicarRetencion] = useState(false);
  // Cobro en colones cuando la(s) factura(s) son USD y el cliente paga en ₡.
  // El diferencial cambiario se calcula prorrateado por factura en el backend.
  const [cobrarEnColones, setCobrarEnColones] = useState(false);
  const [montoCRC, setMontoCRC] = useState('');

  // Retención 2% renta — base = subtotal sin IVA, prorrateado al monto aplicado
  // por factura (FIFO igual que backend). Devuelve {usd, crc} — el CRC usa el T/C
  // efectivo de cada FE para mostrar la retención en ₡ cuando se cobra en colones.
  const retencion = (() => {
    if (!aplicarRetencion) return { usd: 0, crc: 0 };
    const monto = parseFloat(formData.monto) || 0;
    if (monto <= 0) return { usd: 0, crc: 0 };
    const ordenadas = [...invoicesToPay].sort((a, b) => new Date(a.fechaEmision || a.fecha_emision) - new Date(b.fechaEmision || b.fecha_emision));
    let restante = monto;
    let usd = 0, crc = 0;
    for (const f of ordenadas) {
      if (restante <= 0) break;
      const saldo = parseFloat(f.saldoPendiente || f.totalComprobante) || 0;
      const aplicar = Math.min(restante, saldo);
      const subtotal = parseFloat(f.totalVentaNeta || f.subtotal || f.total_venta_neta) || 0;
      const total = parseFloat(f.totalComprobante || f.total_comprobante) || 0;
      const baseProp = total > 0 ? subtotal * (aplicar / total) : subtotal;
      const retFac = Math.round(baseProp * 0.02 * 100) / 100;
      usd += retFac;
      const totalColones = parseFloat(f.totalColones || f.total_colones) || 0;
      const totalUsd = parseFloat(f.totalComprobante || f.total_comprobante) || 0;
      const tcFactura = (totalColones > 0 && totalUsd > 0)
        ? (totalColones / totalUsd)
        : (parseFloat(f.tipoCambio || f.tipo_cambio) || 1);
      crc += retFac * tcFactura;
      restante -= aplicar;
    }
    return { usd, crc: Math.round(crc * 100) / 100 };
  })();
  const retencionTotal = retencion.usd; // compat handleSubmit

  const handleSubmit = async (e) => {
    e.preventDefault();

    const esCobroEnColones = cobrarEnColones && moneda === 'USD';
    if (esCobroEnColones && (!montoCRC || parseFloat(montoCRC) <= 0)) {
      toast.error('Ingrese el monto real recibido en colones');
      return;
    }

    try {
      setLoading(true);

      const data = {
        monto: formData.monto,
        fechaPago: formData.fechaPago,
        medioPago: formData.medioPago,
        numeroComprobante: formData.numeroComprobante,
        banco: formData.banco
      };
      if (aplicarRetencion) data.aplicarRetencion = 'true';
      if (esCobroEnColones) {
        data.pagarEnColones = 'true';
        data.montoCRC = montoCRC;
      }

      if (isMultiple) {
        // Multiple invoice payment — el backend espera FormData; cxcApi.aplicarPagoMultiple
        // construye el FormData internamente con los campos planos + comprobante.
        const fd = new FormData();
        fd.append('facturaIds', JSON.stringify(invoicesToPay.map(f => f.id)));
        fd.append('fechaPago', formData.fechaPago);
        fd.append('montoTotal', formData.monto);
        fd.append('moneda', moneda);
        fd.append('medioPago', formData.medioPago);
        fd.append('numeroComprobante', formData.numeroComprobante || '');
        fd.append('banco', formData.banco || '');
        if (aplicarRetencion) fd.append('aplicarRetencion', 'true');
        if (esCobroEnColones) {
          fd.append('pagarEnColones', 'true');
          fd.append('montoCRC', montoCRC);
        }
        if (file) fd.append('comprobante', file);
        await cxcApi.aplicarPagoMultiple(fd);
        toast.success(`Cobro aplicado a ${invoicesToPay.length} facturas`);
      } else {
        const resp = await cxcApi.aplicarPago(factura.id, data, file);
        toast.success('Cobro registrado correctamente');
        // El backend ahora devuelve warning si el asiento contable falló
        // (cobro guardado OK pero asiento no). Mostrarlo aparte para que el
        // contador lo vea y pueda regenerarlo manual desde Contabilidad.
        if (resp?.data?.warning) {
          toast(resp.data.warning, { icon: '⚠️', duration: 8000, style: { background: '#fef3c7', color: '#92400e' } });
        }
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al registrar cobro');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount, currency = 'CRC') => {
    const num = parseFloat(amount) || 0;
    return currency === 'USD'
      ? `$${num.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
      : `₡${num.toLocaleString('es-CR', { minimumFractionDigits: 2 })}`;
  };

  const saldoRestante = Math.max(0, Math.round((saldoPendiente - (parseFloat(formData.monto) || 0)) * 100) / 100);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-2xl mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-green-600 to-green-700 rounded-t-xl">
          <h3 className="text-lg font-semibold text-white">
            {isMultiple ? `Registrar Pago Múltiple (${invoicesToPay.length})` : 'Registrar Pago'}
          </h3>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {/* Invoice Summary */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="flex items-start justify-between">
            <div>
              {isMultiple ? (
                <>
                  <p className="font-semibold text-gray-900">{invoicesToPay[0]?.receptorNombre}</p>
                  <p className="text-sm text-gray-500 mt-0.5">{invoicesToPay.length} facturas seleccionadas</p>
                </>
              ) : (
                <>
                  <p className="font-semibold text-gray-900">{factura?.receptorNombre}</p>
                  <p className="text-sm text-gray-500 font-mono mt-0.5">{factura?.consecutivo}</p>
                </>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Total a Cobrar</p>
              <p className="text-2xl font-bold text-green-700 ">
                {formatMoney(saldoPendiente, moneda)}
              </p>
            </div>
          </div>

          {isMultiple ? (
            <div className="mt-3 max-h-24 overflow-y-auto">
              <div className="space-y-1">
                {invoicesToPay.map(f => (
                  <div key={f.id} className="flex justify-between text-sm">
                    <span className="font-mono text-gray-600">{f.consecutivo}</span>
                    <span className="text-sm font-medium">{formatMoney(f.saldoPendiente || f.totalComprobante, moneda)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-3 flex gap-6 text-sm">
              <div>
                <span className="text-gray-500">Total factura:</span>
                <span className="ml-2 text-sm font-medium">{formatMoney(factura?.totalComprobante, moneda)}</span>
              </div>
              <div>
                <span className="text-gray-500">Ya cobrado:</span>
                <span className="ml-2 text-sm font-medium">{formatMoney(factura?.totalPagado || 0, moneda)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Pago *
              </label>
              <input
                type="date"
                value={formData.fechaPago}
                onChange={(e) => setFormData({ ...formData, fechaPago: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto a Aplicar *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.monto}
                onChange={(e) => setFormData({ ...formData, monto: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500 "
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Saldo Restante
              </label>
              <div className={`w-full border rounded-lg px-3 py-2  font-semibold ${
                saldoRestante <= 0.01
                  ? 'bg-green-50 text-green-700 border-green-200'
                  : 'bg-yellow-50 text-yellow-700 border-yellow-200'
              }`}>
                {formatMoney(saldoRestante, moneda)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medio de Pago
              </label>
              <select
                value={formData.medioPago}
                onChange={(e) => setFormData({ ...formData, medioPago: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="transferencia">Transferencia</option>
                <option value="efectivo">Efectivo</option>
                <option value="cheque">Cheque</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="sinpe">SINPE Movil</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                No. Comprobante
              </label>
              <input
                type="text"
                value={formData.numeroComprobante}
                onChange={(e) => setFormData({ ...formData, numeroComprobante: e.target.value })}
                placeholder="Referencia"
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Banco
              </label>
              <select
                value={formData.banco}
                onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">Seleccionar banco</option>
                {bancos.map(banco => (
                  <option key={banco.id} value={banco.nombre}>
                    {banco.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comprobante (opcional)
            </label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full border rounded-lg px-2 py-1.5 text-sm file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
            />
          </div>

          {/* Retención 2% renta (cliente entidad del Estado).
              Base = subtotal sin IVA, prorrateado al monto aplicado por factura (FIFO). */}
          <div className="mt-4 p-3 border border-purple-200 rounded-lg bg-purple-50">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={aplicarRetencion}
                onChange={(e) => setAplicarRetencion(e.target.checked)}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm font-medium text-purple-800">Retención 2% renta (entidad del Estado)</span>
            </label>
            {aplicarRetencion && (() => {
              const esCobroCRC = cobrarEnColones && moneda === 'USD';
              const monto = parseFloat(formData.monto) || 0;
              let montoDisp = monto;
              let retDisp = retencion.usd;
              let monedaDisp = moneda;
              if (esCobroCRC) {
                const ordenadas = [...invoicesToPay].sort((a, b) => new Date(a.fechaEmision || a.fecha_emision) - new Date(b.fechaEmision || b.fecha_emision));
                let restante = monto;
                let deudaCRC = 0;
                for (const f of ordenadas) {
                  if (restante <= 0) break;
                  const saldo = parseFloat(f.saldoPendiente || f.totalComprobante) || 0;
                  const aplicar = Math.min(restante, saldo);
                  const totalColones = parseFloat(f.totalColones || f.total_colones) || 0;
                  const totalUsd = parseFloat(f.totalComprobante || f.total_comprobante) || 0;
                  const tcFactura = (totalColones > 0 && totalUsd > 0)
                    ? (totalColones / totalUsd)
                    : (parseFloat(f.tipoCambio || f.tipo_cambio) || 1);
                  deudaCRC += aplicar * tcFactura;
                  restante -= aplicar;
                }
                montoDisp = deudaCRC;
                retDisp = retencion.crc;
                monedaDisp = 'CRC';
              }
              const netoDisp = montoDisp - retDisp;
              return (
                <div className="mt-3 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monto a cobrar{esCobroCRC ? ' (₡)' : ''}:</span>
                    <span className="font-mono">{formatMoney(montoDisp, monedaDisp)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Retención 2%{isMultiple ? ' (prorrateada por factura)' : ''}:</span>
                    <span className="font-mono font-medium text-purple-700">−{formatMoney(retDisp, monedaDisp)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-1">
                    <span className="font-medium text-gray-700">Neto a recibir:</span>
                    <span className="font-mono font-bold text-gray-900">{formatMoney(netoDisp, monedaDisp)}</span>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">
                    La factura se salda al 100%; la retención es crédito fiscal de renta.
                    {esCobroCRC && ' La retención se convierte a ₡ usando el T/C de emisión de cada FE.'}
                  </p>
                </div>
              );
            })()}
          </div>

          {/* Cobrar en Colones — para facturas USD (single o múltiple del mismo cliente).
              Muestra la deuda ₡ REAL (usando T/C guardado al emitir cada FE) para que el
              usuario compare contra lo recibido. Backend recalcula prorrateado. */}
          {moneda === 'USD' && (
            <div className="mt-4 p-3 border border-amber-200 rounded-lg bg-amber-50">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cobrarEnColones}
                  onChange={(e) => { setCobrarEnColones(e.target.checked); if (!e.target.checked) setMontoCRC(''); }}
                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <span className="text-sm font-medium text-amber-800">Cobrar en Colones (₡)</span>
              </label>
              {cobrarEnColones && (() => {
                // Deuda ₡ prorrateada FIFO por cada factura con su T/C efectivo de emisión.
                const monto = parseFloat(formData.monto) || 0;
                const ordenadas = [...invoicesToPay].sort((a, b) => new Date(a.fechaEmision || a.fecha_emision) - new Date(b.fechaEmision || b.fecha_emision));
                let restante = monto;
                let deudaCRCTotal = 0;
                let tcNum = 0;
                let tcDen = 0;
                for (const f of ordenadas) {
                  if (restante <= 0) break;
                  const saldo = parseFloat(f.saldoPendiente || f.totalComprobante) || 0;
                  const aplicar = Math.min(restante, saldo);
                  let retFac = 0;
                  if (aplicarRetencion) {
                    const sub = parseFloat(f.totalVentaNeta || f.subtotal || f.total_venta_neta) || 0;
                    const tot = parseFloat(f.totalComprobante || f.total_comprobante) || 0;
                    const base = tot > 0 ? sub * (aplicar / tot) : sub;
                    retFac = Math.round(base * 0.02 * 100) / 100;
                  }
                  const netoAplicado = aplicar - retFac;
                  const totalColones = parseFloat(f.totalColones || f.total_colones) || 0;
                  const totalUsd = parseFloat(f.totalComprobante || f.total_comprobante) || 0;
                  const tcFactura = (totalColones > 0 && totalUsd > 0)
                    ? (totalColones / totalUsd)
                    : (parseFloat(f.tipoCambio || f.tipo_cambio) || 1);
                  deudaCRCTotal += netoAplicado * tcFactura;
                  tcNum += tcFactura * netoAplicado;
                  tcDen += netoAplicado;
                  restante -= aplicar;
                }
                const tcPromedio = tcDen > 0 ? tcNum / tcDen : 0;
                const cobradoCRC = parseFloat(montoCRC) || 0;
                const diferencial = cobradoCRC - deudaCRCTotal;
                const tipoDif = diferencial >= 0 ? 'Ganancia' : 'Pérdida';
                return (
                  <div className="mt-3 space-y-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {isMultiple ? 'Total facturas en ₡ (T/C emisión)' : 'Total factura en ₡ (T/C emisión)'}
                        </label>
                        <div className="px-3 py-2 bg-white border rounded-lg text-sm font-mono text-gray-700">
                          ₡{deudaCRCTotal.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <p className="text-[10px] text-gray-500 mt-0.5">
                          {isMultiple ? `T/C promedio ponderado: ₡${tcPromedio.toFixed(2)}` : `T/C factura: ₡${tcPromedio.toFixed(2)}`}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Monto recibido en ₡ *</label>
                        <input
                          type="number"
                          step="0.01"
                          value={montoCRC}
                          onChange={(e) => setMontoCRC(e.target.value)}
                          className="w-full border rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="Monto real en colones"
                          required
                        />
                      </div>
                    </div>
                    {cobradoCRC > 0 && (
                      <div className={`text-xs px-3 py-2 rounded font-mono ${diferencial >= 0 ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                        Diferencial cambiario: <strong>{tipoDif} ₡{Math.abs(diferencial).toLocaleString('es-CR', { minimumFractionDigits: 2 })}</strong>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50 font-medium flex items-center gap-2"
            >
              {loading ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></span>
                  Procesando...
                </>
              ) : (
                <>
                  <Check size={18} />
                  {isMultiple ? `Registrar Pago (${invoicesToPay.length})` : 'Registrar Pago'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
